import type { FC } from 'react';
import { memo } from 'react';
import { Icon } from '../../icon-svg/pc-icon';
import { SIDE_BAR_WIDTH } from '../const';
import { useConversationContext } from '../hooks/context';
import { useConversationTools } from '../hooks/hook';
import { ChatStatus } from '../hooks/types';
import { LiteChatWrapper } from './lite-chat-wrapper';

export const ChatPageSkeleton = () => {
  const { navigateToChat } = useConversationContext();
  const { setStatus } = useConversationTools();

  const backToChat = () => {
    navigateToChat('');
    setStatus(ChatStatus.DEFAULT);
  };

  return (
    <div className="flex">
      <div className="relative flex flex-col h-screen overflow-hidden w-full text-normal px-6">
        {/* Header */}
        <div className="flex-shrink-0 py-3 bg-white1/50 backdrop-blur">
          <div className="flex gap-2 mx-auto max-w-6xl">
            <div className="animate-click pt-2">
              <Icon name="IcArrowLeft02" size="small" onClick={backToChat} />
            </div>
            <div className="gap-1 flex-1 min-w-0">
              <div className="h-6 w-48 bg-grey6 rounded animate-pulse" />
              <div className="h-4 w-32 bg-grey6 rounded animate-pulse mt-1" />
            </div>
          </div>
        </div>

        <div className="px-8">
          {/* Main Content */}
          <ChatItemSkeleton />
        </div>
      </div>
      <LiteChatWrapper />
    </div>
  );
};

interface ChatItemSkeletonProps {
  showSideBar?: boolean;
}
export const ChatItemSkeleton: FC<ChatItemSkeletonProps> = memo((props) => {
  const { showSideBar = true } = props;

  return (
    <div className="flex flex-1 h-full mx-auto w-full">
      <div className="flex w-full relative animate-fade-in justify-center">
        <div className="space-y-8 w-full flex-1 max-w-2xl">
          <div className="space-y-4">
            <h3 className="text-h3 flex items-center space-x-2">
              <Icon name="IcLink" className="text-grey3" size="middle" />
              <div className="h-6 w-24 bg-grey7 rounded animate-pulse" />
            </h3>
            <div className="gap-2 grid grid-cols-2">
              <div className="h-10 bg-grey7 rounded animate-pulse" />
              <div className="h-10 bg-grey7 rounded animate-pulse" />
              <div className="h-10 bg-grey7 rounded animate-pulse" />
              <div className="h-10 bg-grey7 rounded animate-pulse" />
            </div>
          </div>
          <div className="space-y-4">
            <h3 className="text-h3 font-medium space-x-2 flex items-center">
              <Icon name="IcAiFill" className="text-ai_color" size="middle" />
              <div className="h-6 w-24 bg-grey7 rounded animate-pulse" />
            </h3>
            <div className="space-y-4">
              <div className="h-4 bg-grey7 rounded animate-pulse" />
              <div className="h-4 bg-grey7 rounded animate-pulse w-3/4" />
              <div className="h-4 bg-grey7 rounded animate-pulse w-1/2" />
            </div>
            <div className="w-full h-px bg-grey6" />
            <div className="text-t2 text-grey3 space-y-2">
              <div className="h-4 bg-grey7 rounded animate-pulse w-40" />
              <div className="h-4 bg-grey7 rounded animate-pulse w-40" />
            </div>
          </div>
        </div>
      </div>
      {showSideBar && <div className="flex-shrink-0" style={{ width: SIDE_BAR_WIDTH }} />}
    </div>
  );
});

export const LiteChatSkeleton: FC = memo(() => {
  return (
    <div className="h-screen w-full flex-shrink-0" style={{ width: SIDE_BAR_WIDTH }}>
      <div className="fixed z-10 border h-full" style={{ width: SIDE_BAR_WIDTH }}>
        <div className="flex flex-col h-full">
          <div className="flex-shrink-0 p-3 border-b">
            <div className="h-6 w-32 bg-grey6 rounded animate-pulse" />
          </div>
          <div className="flex-1 p-4 space-y-4">
            <div className="h-4 bg-grey6 rounded animate-pulse w-3/4" />
            <div className="h-4 bg-grey6 rounded animate-pulse w-1/2" />
            <div className="h-4 bg-grey6 rounded animate-pulse w-2/3" />
          </div>
        </div>
      </div>
    </div>
  );
});

export const LiteChatItemSkeleton: FC = memo(() => {
  return (
    <div className="space-y-6 text-t2">
      <div className="space-y-2">
        <div className="bg-black1/90 text-white px-4 rounded-lg rounded-br-none"></div>
      </div>
      <div className="space-y-4">
        <div className="h-4 bg-grey7 rounded animate-pulse" />
        <div className="h-4 bg-grey7 rounded animate-pulse w-3/4" />
        <div className="h-4 bg-grey7 rounded animate-pulse w-1/2" />
      </div>
      <div className="flex items-center justify-between text-grey3">
        <div className="flex items-center space-x-4">
          <div className="w-5 h-5 bg-grey7 rounded animate-pulse" />
          <div className="w-5 h-5 bg-grey7 rounded animate-pulse" />
        </div>
        <div className="flex items-center space-x-4">
          <div className="w-5 h-5 bg-grey7 rounded animate-pulse" />
          <div className="w-5 h-5 bg-grey7 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
});
